import React, { useEffect, useState } from 'react';
import { ChartBar, Eye, GlobeHemisphereWest } from '@phosphor-icons/react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAudience = () => {
  const [audience, setAudience] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAudience = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/audience`);
        setAudience(response.data);
      } catch (requestError) {
        setError('Impossible de charger les statistiques de fréquentation.');
      } finally {
        setLoading(false);
      }
    };
    fetchAudience();
  }, []);

  const formatLastSeen = (value) => value ? new Date(value).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '—';

  const stats = [
    { label: 'Visites aujourd’hui', value: audience?.today_views ?? 0, icon: Eye, color: '#E63946', testId: 'audience-today-views' },
    { label: 'Visites sur 30 jours', value: audience?.total_views ?? 0, icon: ChartBar, color: '#2A9D8F', testId: 'audience-total-views' },
    { label: 'Adresses IP distinctes', value: audience?.distinct_ips ?? 0, icon: GlobeHemisphereWest, color: '#E9C46A', testId: 'audience-ip-count' },
  ];

  return (
    <AdminLayout title="Audience">
      <div className="mb-8" data-testid="audience-page-heading">
        <p className="text-gray-600 text-sm">Fréquentation des pages publiques sur les 30 derniers jours.</p>
        <p className="text-gray-600 text-xs mt-1">Les adresses IP publiques sont enregistrées après consentement et conservées 30 jours.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64" data-testid="audience-loading"><div className="w-8 h-8 border-2 border-[#E63946] border-t-transparent rounded-full animate-spin" /></div>
      ) : error ? (
        <div className="border border-[#E63946]/30 bg-[#E63946]/5 text-[#B4232F] p-4 text-sm" role="alert" data-testid="audience-error">{error}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-testid="audience-summary">
            {stats.map((stat) => {
              const Icon = stat.icon;
              return <div key={stat.label} className="bg-white border border-gray-200 p-6 shadow-sm" data-testid={stat.testId}>
                <div className="flex items-start justify-between">
                  <div><p className="text-gray-500 text-sm mb-2">{stat.label}</p><p className="font-['Outfit'] text-3xl font-bold text-gray-900">{stat.value}</p></div>
                  <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: `${stat.color}18` }}><Icon size={24} style={{ color: stat.color }} /></div>
                </div>
              </div>;
            })}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            <section className="bg-white border border-gray-200 shadow-sm" data-testid="audience-pages-section">
              <div className="p-6 border-b border-gray-200"><h2 className="font-['Outfit'] text-lg font-bold text-gray-900">Pages les plus consultées</h2></div>
              <div className="divide-y divide-gray-100">
                {audience.top_pages?.length ? audience.top_pages.map((page, index) => <div key={page.path} className="flex items-center justify-between gap-4 p-4" data-testid={`audience-page-row-${index}`}><span className="text-gray-700 text-sm font-mono truncate">{page.path}</span><span className="text-[#2A9D8F] font-bold text-sm">{page.views} visite{page.views > 1 ? 's' : ''}</span></div>) : <p className="p-8 text-center text-gray-600 text-sm" data-testid="audience-pages-empty">Aucune visite enregistrée pour le moment.</p>}
              </div>
            </section>

            <section className="bg-white border border-gray-200 shadow-sm" data-testid="audience-ips-section">
              <div className="p-6 border-b border-gray-200"><h2 className="font-['Outfit'] text-lg font-bold text-gray-900 flex items-center gap-2"><GlobeHemisphereWest size={20} className="text-[#E63946]" />Adresses IP récentes</h2></div>
              <div className="divide-y divide-gray-100">
                {audience.recent_ips?.length ? audience.recent_ips.map((visitor, index) => <div key={visitor.ip_address} className="flex items-center justify-between gap-4 p-4" data-testid={`audience-ip-row-${index}`}><div className="min-w-0"><p className="text-gray-900 text-sm font-mono truncate" data-testid={`audience-ip-value-${index}`}>{visitor.ip_address}</p><p className="text-gray-600 text-xs mt-1" data-testid={`audience-ip-last-seen-${index}`}>Dernière visite : {formatLastSeen(visitor.last_seen)}</p></div><span className="text-[#E63946] font-bold text-sm">{visitor.views} visite{visitor.views > 1 ? 's' : ''}</span></div>) : <p className="p-8 text-center text-gray-600 text-sm" data-testid="audience-ips-empty">Aucune adresse IP publique enregistrée pour le moment.</p>}
              </div>
            </section>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminAudience;